/**
 * Altair Agent Component
 * 
 * New version of Altair using the multi-agent architecture.
 * This component demonstrates how to integrate the agent system.
 * 
 * Key differences from original Altair.tsx:
 * 1. Uses AgentProvider instead of LiveAPIProvider
 * 2. Uses useAgentContext instead of useLiveAPIContext
 * 3. No monolithic system instruction - agents handle their own
 * 4. Tool calls are handled by agents, not component
 * 5. State management is cleaner with agent transitions
 * 
 * To use this:
 * 1. Replace LiveAPIProvider with AgentProvider in App.tsx
 * 2. Replace Altair component with AltairAgent
 */

import { useEffect, useRef, useState, memo } from 'react';
import { useAgentContext } from '../../contexts/AgentContext';
import { useTranscript } from '../../contexts/TranscriptContext';
import { AllPermissions } from '../../utils/permissions';
import ReportInput from '../report-input/ReportInput';
import VehicleSelector from '../vehicle-selector/VehicleSelector';
import { logger } from '../../utils/logger';
import { config } from '../../config/runtime-config';
import { getEmbeddedHandler } from '../../services/embedded-handler';

// Accident Flow components
import AccidentOptions from '../accident-options/AccidentOptions';
import CameraArrow from '../camera-arrow/CameraArrow';
import ImageUpload from '../image-upload/ImageUpload';
import AccidentDataReview from '../accident-data-review/AccidentDataReview';

// Lost Report Flow components
import LicenseUpload from '../license-upload/LicenseUpload';
import LicenseDataReview from '../license-data-review/LicenseDataReview';
import DateRangePicker from '../date-range-picker/DateRangePicker';
import ReportsList from '../reports-list/ReportsList';
import CallSaaedOffice from '../call-saaed-office/CallSaaedOffice';

// Utils for lost report flow
import { mapPlateData } from '../../utils/plate-mapper';
import { enrichReportsWithLabels } from '../../utils/plate-data-reverse-mapper';
import { searchAccidentReports } from '../../services/accident-reports-api';
import { SearchReportsRequest, EnrichedAccidentReport } from '../../types/accident-reports';
import { analyzeAccidentScene, extractLicensePlate, extractVideoFrames } from '../../services/visionService';

interface AltairAgentProps {
  permissions?: AllPermissions | null;
  userData?: { username: string; mobileNumber: string } | null;
  voiceGender?: 'male' | 'female';
  onLogout?: () => void;
  videoStream?: MediaStream | null;
  embeddedMode?: boolean;                    // NEW: Running in iframe mode
  initialAgent?: string;                     // NEW: Jump to specific agent on connect
  initialContext?: Record<string, any>;      // NEW: Initial context for deep linking
  onFlowComplete?: (result: any) => void;    // NEW: Callback when flow completes
}

/**
 * Helper function to convert a File to base64 string
 * Used for sending images to Gemini for vision analysis
 */
const fileToBase64 = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      if (e.target?.result && typeof e.target.result === 'string') {
        resolve(e.target.result.split(',')[1]); // Remove data URL prefix
      } else {
        reject(new Error('Failed to read file'));
      }
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
};

function AltairAgentComponent({ 
  permissions, 
  userData, 
  voiceGender = 'male', 
  onLogout,
  videoStream,
  embeddedMode = false,
  initialAgent,
  initialContext,
  onFlowComplete
}: AltairAgentProps) {
  
  const {
    connected,
    currentAgentId,
    context,
    isTransitioning,
    setConfig,
    isInitialized,
    send,
    sendRealtimeInput,
    updateTransitionData,
    transitionTo,
    client,
    manager,
  } = useAgentContext();
  
  // Transcript management
  const { addMessage } = useTranscript();
  
  // Embedded mode handler
  const embeddedHandler = embeddedMode ? getEmbeddedHandler(true) : null;
  
  // UI State - derived from current agent and context
  // Renewal flow states
  const [showReportInput, setShowReportInput] = useState(false);
  const [showVehicleSelector, setShowVehicleSelector] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [showResult, setShowResult] = useState(false);
  const [reportError, setReportError] = useState<string | null>(null);
  const [isLoadingReport, setIsLoadingReport] = useState(false);
  
  // Accident flow states
  const [showAccidentOptions, setShowAccidentOptions] = useState(false); // NEW: Option selection UI
  const [showCameraArrow, setShowCameraArrow] = useState(false); // NEW: Camera arrow indicator
  const [showImageUpload, setShowImageUpload] = useState(false);
  const [uploadedImages, setUploadedImages] = useState<File[]>([]);
  const [showAccidentConfirm, setShowAccidentConfirm] = useState(false);
  const [showAccidentResult, setShowAccidentResult] = useState(false);
  const [accidentResult, setAccidentResult] = useState<any>(null);
  
  // Vision processing state — used to show a loading indicator and tell AI to wait
  const [isProcessingImages, setIsProcessingImages] = useState(false);
  const [processingImageLabel, setProcessingImageLabel] = useState('');

  // Lost report flow states
  const [showLicenseUpload, setShowLicenseUpload] = useState(false);
  const [showLicenseReview, setShowLicenseReview] = useState(false);
  const [extractedLicenseData, setExtractedLicenseData] = useState<any>(null);
  const [confirmedMappedIds, setConfirmedMappedIds] = useState<any>(null);
  const confirmedMappedIdsRef = useRef<any>(null); // Ref to track latest mapped IDs
  const [showDateRangePicker, setShowDateRangePicker] = useState(false);
  const [showReportsList, setShowReportsList] = useState(false);
  const [showCallSaaed, setShowCallSaaed] = useState(false);
  const [searchResults, setSearchResults] = useState<{ Count: number; Data: EnrichedAccidentReport[] } | null>(null);
  const [isSearching, setIsSearching] = useState(false);
  const [reportCountForCall, setReportCountForCall] = useState(0);
  
  // Get language from context
  const detectedLanguage = context?.transitionData.language || 'en';
  
  // Set config on mount - connection will be triggered by ControlTray's connect button
  useEffect(() => {
    if (userData) {
      logger.connection('[AltairAgent] Setting config with user:', userData.username);
      
      // Merge initial context if provided (for deep linking)
      const configData: any = {
        userData,
        voiceGender,
        userLocation: undefined, // Will be set when location is detected
      };
      
      // Merge initial context from embedded mode
      if (initialContext) {
        logger.info('[AltairAgent] Merging initial context for deep linking:', initialContext);
        Object.assign(configData, initialContext);
      }
      
      setConfig(configData);
    }
  }, [userData, voiceGender, setConfig, initialContext]);
  
  // Setup embedded mode handlers
  useEffect(() => {
    if (!embeddedHandler) return;
    
    // Handle launch requests from parent
    embeddedHandler.onLaunch((agent: string, context?: Record<string, any>) => {
      logger.info('[AltairAgent] Embedded launch request:', { agent, context });
      
      if (context) {
        updateTransitionData(context);
      }
      
      if (connected) {
        transitionTo(agent, 'embedded_launch', context);
      }
    });
    
    // Handle context updates from parent
    embeddedHandler.onContextUpdate((context: Record<string, any>) => {
      logger.info('[AltairAgent] Embedded context update:', context);
      updateTransitionData(context);
    });
    
    // Handle close requests from parent
    embeddedHandler.onClose(() => {
      logger.info('[AltairAgent] Embedded close request');
      // Could disconnect or notify parent
    });
  }, [embeddedHandler, connected, transitionTo, updateTransitionData]);
  
  // Listen for flow completion events from AgentManager
  useEffect(() => {
    if (!manager || !embeddedHandler) return;
    
    const handleFlowComplete = (flowType: string, result: any, agentId: string) => {
      logger.info('[AltairAgent] Flow completed:', { flowType, result, agentId });
      
      // Send result to parent window
      embeddedHandler.sendResult(result, agentId);
      
      // Call optional callback
      if (onFlowComplete) {
        onFlowComplete(result);
      }
    };
    
    manager.on('flowCompleted', handleFlowComplete);
    
    return () => {
      manager.off('flowCompleted', handleFlowComplete);
    };
  }, [manager, embeddedHandler, onFlowComplete]);
  
  // Handle deep linking to initial agent after connection (embedded mode)
  useEffect(() => {
    if (connected && initialAgent && initialAgent !== 'greeting_agent' && currentAgentId === 'greeting_agent') {
      logger.info(`[AltairAgent] Deep linking to ${initialAgent} after connection`);
      
      // Small delay to ensure connection is stable
      const timer = setTimeout(() => {
        transitionTo(initialAgent, 'deep_link', initialContext);
      }, 500);
      
      return () => clearTimeout(timer);
    }
  }, [connected, initialAgent, currentAgentId, initialContext, transitionTo]);
  
  // Send status updates to parent in embedded mode
  useEffect(() => {
    if (!embeddedHandler) return;
    
    // Determine status based on connection and agent state
    let status: 'idle' | 'listening' | 'speaking' = 'idle';
    
    // This is a simplified status - you might want to track actual mic/speaker state
    if (connected && !isTransitioning) {
      status = 'idle'; // Could be enhanced to detect actual listening/speaking
    }
    
    embeddedHandler.sendStatus(status, currentAgentId || undefined);
  }, [embeddedHandler, connected, isTransitioning, currentAgentId]);
  
  // Get user location and update context
  useEffect(() => {
    if (permissions?.location && navigator.geolocation && connected) {
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const lat = position.coords.latitude;
          const lng = position.coords.longitude;
          
          try {
            const response = await fetch(
              `${config.REACT_APP_NOMINATIM_API_URL}/reverse?format=json&lat=${lat}&lon=${lng}&accept-language=ar`
            );
            const data = await response.json();
            
            const address = data.address;
            const addressParts = [];
            if (address.road) addressParts.push(address.road);
            if (address.suburb || address.neighbourhood) addressParts.push(address.suburb || address.neighbourhood);
            if (address.city || address.town) addressParts.push(address.city || address.town);
            if (address.state) addressParts.push(address.state);
            if (address.country) addressParts.push(address.country);
            
            const arabicAddress = addressParts.length > 0 ? addressParts.join('، ') : data.display_name;
            
            // Update agent context with location
            updateTransitionData({
              userLocation: { lat, lng, address: arabicAddress },
            });
            
            logger.ui('[AltairAgent] Location updated:', arabicAddress);
            
            // Notify AI of user's location (so it knows the address for accident reports)
            send(`[معلومة للنظام فقط - لا تقرأها] تم تحديد موقع المستخدم تلقائياً: ${arabicAddress}`, false);
          } catch (error) {
            logger.error('[AltairAgent] Geocoding failed:', error);
          }
        },
        (error) => {
          logger.error('[AltairAgent] Location error:', error);
        }
      );
    }
  }, [permissions, connected, updateTransitionData]);
  
  // Update UI state based on current agent
  useEffect(() => {
    // Reset all UI states FIRST
    // Renewal flow
    setShowReportInput(false);
    setShowVehicleSelector(false);
    setShowConfirmation(false);
    setShowResult(false);
    setReportError(null);
    setIsLoadingReport(false);
    // Accident flow
    setShowAccidentOptions(false);
    setShowCameraArrow(false);
    setShowImageUpload(false);
    setShowAccidentConfirm(false);
    setShowAccidentResult(false);
    // Lost report flow
    setShowLicenseUpload(false);
    setShowLicenseReview(false);
    setShowDateRangePicker(false);
    setShowReportsList(false);
    setShowCallSaaed(false);
    
    // Show appropriate UI based on current agent (only if not router/faq/greeting)
    switch (currentAgentId) {
      // Renewal flow agents
      case 'renewal_input_agent':
        setShowReportInput(true);
        break;
      case 'renewal_vehicle_agent':
        setShowVehicleSelector(true);
        break;
      case 'renewal_confirm_agent':
        setShowConfirmation(true);
        break;
      case 'renewal_result_agent':
        setShowResult(true);
        break;
      
      // Accident flow agents
      case 'accident_start_agent':
        // Don't auto-show anything - agent will call show_documentation_options
        break;
      case 'accident_collect_submit_agent':
        // UI state managed by tool calls (show_confirmation)
        // show_confirmation sets showAccidentConfirm
        break;
      
      // Lost report flow agents
      case 'lost_report_upload_extract_agent':
        // Auto-show license upload UI when agent activates
        // This ensures UI appears even when transitioning from other agents
        setShowLicenseUpload(true);
        break;
      case 'lost_report_search_agent':
        setShowDateRangePicker(true);
        break;
      case 'lost_report_result_agent':
        // Will show either ReportsList or CallSaaed based on search results
        const results = context?.transitionData.searchResults;
        if (results?.count === 1) {
          setShowReportsList(true);
          setShowCallSaaed(false); // Ensure call screen is hidden for single report
        } else if (results && results.count !== undefined) {
          // Only show CallSaaed if we have valid results data
          setReportCountForCall(results.count || 0);
          setShowCallSaaed(true);
          setShowReportsList(false); // Ensure reports list is hidden for 0 or multiple reports
        }
        // If no results data, don't show anything (already reset above)
        break;
      
      // Agents that should have NO UI
      case 'router_agent':
      case 'faq_agent':
      case 'greeting_agent':
        // All UIs already reset above, nothing to show
        break;
      
      default:
        // All UIs already reset above
        break;
    }
    
    logger.ui('[AltairAgent] Current agent:', currentAgentId);
  }, [currentAgentId, context?.transitionData.searchResults]);
  
  // Note: Image is now sent immediately when uploaded in handleLicenseImageUpload
  // No need for separate useEffect since we stay in the same agent
  
  // Monitor video stream to hide camera arrow when camera opens
  useEffect(() => {
    if (videoStream && showCameraArrow) {
      logger.ui('[AltairAgent] Camera opened - hiding arrow');
      setShowCameraArrow(false);
    }
  }, [videoStream, showCameraArrow]);
  
  // Additional monitoring on agent change to ensure arrow is hidden when camera is active
  useEffect(() => {
    if (currentAgentId === 'accident_start_agent' && videoStream && showCameraArrow) {
      logger.ui('[AltairAgent] Camera active on agent change - hiding arrow');
      setShowCameraArrow(false);
    }
  }, [currentAgentId, videoStream, showCameraArrow]);
  
  // Listen for tool calls from the manager
  useEffect(() => {
    if (!manager) return;
    
    const handleToolCall = (result: any) => {
      logger.tool('[AltairAgent] Tool call result:', result);
      
      // Handle UI-triggering tools
      // Renewal flow
      if (result.toolName === 'show_report_input') {
        setShowReportInput(true);
      }
      
      // Accident flow
      if (result.toolName === 'show_documentation_options') {
        setShowAccidentOptions(true);
      }
      
      if (result.toolName === 'select_documentation_method') {
        const method = result.args?.method || result.result?.method;
        setShowAccidentOptions(false); // Hide options UI
        if (method === 'upload_images') {
          setShowImageUpload(true);
        } else if (method === 'live_camera') {
          setShowCameraArrow(true);
        }
      }
      
      if (result.toolName === 'show_camera_arrow') {
        setShowCameraArrow(true);
      }
      
      if (result.toolName === 'show_image_upload') {
        setShowImageUpload(true);
      }
      
      if (result.toolName === 'collect_accident_info') {
        // Update transition data with collected accident info
        if (result.result?.accident_data) {
          updateTransitionData({ accidentData: result.result.accident_data });
        }
      }
      
      if (result.toolName === 'show_confirmation') {
        setShowAccidentConfirm(true);
      }
      
      // Lost report flow
      if (result.toolName === 'show_license_upload') {
        setShowLicenseUpload(true);
      }
      
      if (result.toolName === 'extract_license_data') {
        // Store extracted data for review
        const extracted = result.args;
        setExtractedLicenseData(extracted);
        setShowLicenseReview(true);
        setShowLicenseUpload(false); // Hide upload UI when review UI shows
      }
      
      if (result.toolName === 'show_date_picker') {
        setShowDateRangePicker(true);
      }
    };
    
    manager.on('toolCall', handleToolCall);
    
    return () => {
      manager.off('toolCall', handleToolCall);
    };
  }, [manager]);
  
  // Capture conversation transcript from Gemini Live API transcriptions
  useEffect(() => {
    if (!client) return;
    
    // Buffers to accumulate partial transcriptions
    let userTranscriptBuffer = '';
    let aiTranscriptBuffer = '';
    let userFlushTimer: number | null = null;
    let aiFlushTimer: number | null = null;
    
    // Flush user buffer
    const flushUserBuffer = () => {
      if (userTranscriptBuffer.trim()) {
        logger.info('[Transcript] User complete:', userTranscriptBuffer);
        addMessage({
          role: 'user',
          content: userTranscriptBuffer.trim(),
        });
        userTranscriptBuffer = '';
      }
    };
    
    // Flush AI buffer
    const flushAIBuffer = () => {
      if (aiTranscriptBuffer.trim()) {
        logger.info('[Transcript] AI complete:', aiTranscriptBuffer);
        addMessage({
          role: 'assistant',
          content: aiTranscriptBuffer.trim(),
          agentId: currentAgentId || undefined,
        });
        aiTranscriptBuffer = '';
      }
    };
    
    // Handle user speech transcriptions (partial chunks)
    const handleUserTranscription = (text: string) => {
      logger.info('[Transcript] User partial:', text);
      userTranscriptBuffer += (userTranscriptBuffer ? ' ' : '') + text;
      
      // Debounce: flush after 800ms of no new chunks
      if (userFlushTimer) {
        clearTimeout(userFlushTimer);
      }
      userFlushTimer = window.setTimeout(() => {
        flushUserBuffer();
        userFlushTimer = null;
      }, 800);
    };
    
    // Handle AI speech transcriptions (partial chunks)
    const handleAITranscription = (text: string) => {
      logger.info('[Transcript] AI partial:', text);
      
      // IMPORTANT: When AI starts speaking, flush user buffer immediately
      if (userTranscriptBuffer.trim()) {
        if (userFlushTimer) {
          clearTimeout(userFlushTimer);
          userFlushTimer = null;
        }
        flushUserBuffer();
      }
      
      aiTranscriptBuffer += (aiTranscriptBuffer ? ' ' : '') + text;
      
      // Debounce: flush after 800ms of no new chunks
      if (aiFlushTimer) {
        clearTimeout(aiFlushTimer);
      }
      aiFlushTimer = window.setTimeout(() => {
        flushAIBuffer();
        aiFlushTimer = null;
      }, 800);
    };
    
    // Handle turn complete - flush both buffers immediately
    const handleTurnComplete = () => {
      logger.info('[Transcript] Turn complete - flushing all buffers');
      
      // Clear any pending timers
      if (userFlushTimer) {
        clearTimeout(userFlushTimer);
        userFlushTimer = null;
      }
      if (aiFlushTimer) {
        clearTimeout(aiFlushTimer);
        aiFlushTimer = null;
      }
      
      // Flush both buffers
      flushUserBuffer();
      flushAIBuffer();
    };
    
    client.on('inputTranscription', handleUserTranscription);
    client.on('outputTranscription', handleAITranscription);
    client.on('turncomplete', handleTurnComplete);
    
    return () => {
      // Cleanup timers
      if (userFlushTimer) clearTimeout(userFlushTimer);
      if (aiFlushTimer) clearTimeout(aiFlushTimer);
      
      client.off('inputTranscription', handleUserTranscription);
      client.off('outputTranscription', handleAITranscription);
      client.off('turncomplete', handleTurnComplete);
    };
  }, [client, addMessage, currentAgentId]);
  
  // === RENEWAL FLOW HANDLERS ===
  
  const handleReportNumberSubmit = async (reportNumber: string) => {
    logger.ui('[AltairAgent] Report number submitted:', reportNumber);
    
    // Clear any previous error and set loading
    setReportError(null);
    setIsLoadingReport(true);
    
    try {
      // Fetch report data
      const response = await fetch('https://saaed360test.saaed.ae/EServices/api/objection/report', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: `ReportNumber=${reportNumber}`,
      });
      
      const data = await response.json();
      
      if (data.Result !== 1 || !data.Vehicles || data.Vehicles.length === 0) {
        // Set error message based on language
        const errorMessage = detectedLanguage === 'ar'
          ? 'لم يتم العثور على التقرير. يرجى التحقق من الرقم والمحاولة مرة أخرى.'
          : detectedLanguage === 'ur'
          ? 'رپورٹ نہیں ملی۔ براہ کرم نمبر چیک کریں اور دوبارہ کوشش کریں۔'
          : 'Report not found. Please check the number and try again.';
        
        setReportError(errorMessage);
        setIsLoadingReport(false);
        
        // Notify agent silently — UI already shows the error
        send(`[System info - do not read aloud] Report not found for number: ${reportNumber}. The UI is showing an error message to the user.`, false);
        return;
      }
      
      // Clear error on success
      setReportError(null);
      setIsLoadingReport(false);
      
      // Update context with report data
      updateTransitionData({ reportData: data });
      
      // Notify agent silently — transition handles the announcement
      send(`[System info - do not read aloud] Report found with ${data.Vehicles.length} vehicles. Transitioning to vehicle selection.`, false);
      
      // Trigger transition to vehicle agent
      await transitionTo('renewal_vehicle_agent', 'report_found', { reportData: data });
      
    } catch (error) {
      logger.error('[AltairAgent] Report fetch failed:', error);
      
      // Set error message for network/server errors
      const errorMessage = detectedLanguage === 'ar'
        ? 'فشل الاتصال بالخادم. يرجى التحقق من الإنترنت والمحاولة مرة أخرى.'
        : detectedLanguage === 'ur'
        ? 'سرور سے کنکشن ناکام۔ براہ کرم انٹرنیٹ چیک کریں اور دوبارہ کوشش کریں۔'
        : 'Failed to connect to server. Please check your internet and try again.';
      
      setReportError(errorMessage);
      setIsLoadingReport(false);
      
      // Notify agent silently — UI already shows the error
      send(`[System info - do not read aloud] Report lookup failed. The UI is showing an error message to the user.`, false);
    }
  };
  
  const handleVehicleSelect = async (vehicle: any) => {
    logger.ui('[AltairAgent] Vehicle selected:', vehicle.Plate.Number);
    
    // Update context
    updateTransitionData({ selectedVehicle: vehicle });
    
    // Transition to confirm agent
    await transitionTo('renewal_confirm_agent', 'vehicle_selected', { selectedVehicle: vehicle });
  };
  
  const handleRenewalConfirm = async () => {
    logger.ui('[AltairAgent] Renewal confirmed, submitting...');
    
    const reportData = context?.transitionData.reportData;
    const selectedVehicle = context?.transitionData.selectedVehicle;
    
    if (!reportData || !selectedVehicle || !userData) {
      logger.error('[AltairAgent] Missing data for renewal');
      return;
    }
    
    try {
      const requestData = {
        ObjectionRequestTypeId: 4,
        RequestType: "True copy of traffic accident report",
        Channel: 1,
        searchResult: reportData,
        selectedVehicle: selectedVehicle,
        selectedReport: reportData.Reports?.[0] || reportData,
        BasicInfo: {
          Name: userData.username,
          Mobile: userData.mobileNumber,
          PereferredCommunicationMethod: 2,
        },
        Documents: [],
        RecaptchaToken: "",
      };
      
      logger.api('[AltairAgent] Submitting renewal request:', requestData);
      
      const response = await fetch('https://saaed360test.saaed.ae/EServices/api/objection/RequestForDemo', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestData),
      });
      
      const result = await response.json();
      logger.api('[AltairAgent] Renewal API response:', result);
      
      // Check if API returned an error (Result === 0 or RequestId === 0/null)
      // This matches how the original Altair.tsx handles it
      if (result.Result === 0 || !result.RequestId || result.RequestId === 0) {
        logger.error('[AltairAgent] API returned error:', result);
        
        // Transform result to include error flag
        const errorResult = {
          error: true,
          Result: result.Result,
          RequestId: result.RequestId,
          Message: result.Message || (detectedLanguage === 'ar' 
            ? 'حدث خطأ أثناء معالجة الطلب' 
            : detectedLanguage === 'ur'
            ? 'درخواست پر کارروائی کے دوران ایک خرابی ہوئی'
            : 'An error occurred while processing the request'),
        };
        
        updateTransitionData({ renewalResult: errorResult });
        await transitionTo('renewal_result_agent', 'submission_complete', { renewalResult: errorResult });
        return;
      }
      
      // Success case - pass result as-is
      logger.info('[AltairAgent] Renewal successful:', result.RequestId);
      updateTransitionData({ renewalResult: result });
      await transitionTo('renewal_result_agent', 'submission_complete', { renewalResult: result });
      
    } catch (error) {
      logger.error('[AltairAgent] Renewal submission failed:', error);
      const errorResult = {
        error: true, 
        Message: detectedLanguage === 'ar' 
          ? 'فشل الاتصال بالخادم. يرجى التحقق من اتصال الإنترنت والمحاولة مرة أخرى.'
          : detectedLanguage === 'ur'
          ? 'سرور سے کنکشن ناکام ہو گیا۔ براہ کرم اپنا انٹرنیٹ کنکشن چیک کریں اور دوبارہ کوشش کریں۔'
          : 'Failed to connect to server. Please check your internet connection and try again.'
      };
      updateTransitionData({ renewalResult: errorResult });
      await transitionTo('renewal_result_agent', 'submission_complete', { renewalResult: errorResult });
    }
  };
  
  const handleNewRequest = async () => {
    // Clear data and go back to input
    updateTransitionData({
      reportData: undefined,
      selectedVehicle: undefined,
      renewalResult: undefined,
    });
    await transitionTo('renewal_input_agent', 'new_request');
  };
  
  const handleBackToMenu = async () => {
    // Clear all renewal data and go to router
    updateTransitionData({
      reportData: undefined,
      selectedVehicle: undefined,
      renewalResult: undefined,
    });
    await transitionTo('router_agent', 'back_to_menu');
  };
  
  // === ACCIDENT FLOW HANDLERS ===

  /** Returns a language-appropriate "please wait, processing images" phrase */
  const getProcessingWaitPhrase = (lang: string) => {
    if (lang === 'ar') return 'لحظة، أنا أحلل صورك الآن، انتظر من فضلك.';
    if (lang === 'ur') return 'ایک لمحہ، میں ابھی آپ کی تصاویر کا تجزیہ کر رہا ہوں، براہ کرم انتظار کریں۔';
    return 'Please wait a moment — I am analyzing your images right now.';
  };

  const handleImagesUploaded = async (files: File[]) => {
    logger.ui('[AltairAgent] Images uploaded:', files.length);
    setUploadedImages(files);

    // Store count and mark vision as NOT yet complete — blocks premature proceed_to_collect
    updateTransitionData({ uploadedImageCount: files.length, visionAnalysisComplete: false });
    logger.ui('[AltairAgent] Stored image count in transitionData:', files.length, '— vision NOT complete yet');

    const lang = (detectedLanguage || 'ar') as 'ar' | 'en' | 'ur';

    // Collect all base64 frames (images directly, videos via frame extraction)
    const allImages: string[] = [];
    const allMimeTypes: string[] = [];

    for (const file of files) {
      try {
        if (file.type.startsWith('video/')) {
          // Extract 5 evenly-spaced frames from video
          logger.ui(`[AltairAgent] Extracting frames from video: ${file.name}`);
          const frames = await extractVideoFrames(file, 5);
          frames.forEach(f => {
            allImages.push(f.data);
            allMimeTypes.push(f.mimeType);
          });
          logger.ui(`[AltairAgent] Extracted ${frames.length} frames from ${file.name}`);
        } else {
          // Regular image → base64
          const base64 = await fileToBase64(file);
          allImages.push(base64);
          allMimeTypes.push(file.type || 'image/jpeg');
          logger.ui(`[AltairAgent] Image processed: ${file.name}`);
        }
      } catch (error) {
        logger.error(`[AltairAgent] Error processing file ${file.name}:`, error);
      }
    }

    if (allImages.length > 0) {
      const processingLabel = lang === 'ar'
        ? `⏳ جاري تحليل ${files.length} صورة...`
        : lang === 'ur'
        ? `⏳ ${files.length} تصاویر کا تجزیہ جاری ہے...`
        : `⏳ Analyzing ${files.length} image(s)...`;

      setIsProcessingImages(true);
      setProcessingImageLabel(processingLabel);

      // ── FREEZE: cancel any in-progress AI response, clear buffered audio,
      //    and mute the microphone so the AI cannot speak or receive new input
      //    while the vision model is working.
      manager?.cancelCurrentResponse();
      manager?.clearInputAudioBuffer();
      window.dispatchEvent(new CustomEvent('vision:freeze'));
      logger.ui('[AltairAgent] Vision freeze — AI response cancelled, mic muted, audio buffer cleared');

      try {
        logger.ui(`[AltairAgent] Sending ${allImages.length} image(s)/frame(s) to vision model`);

        const result = await analyzeAccidentScene({
          backendUrl: config.REACT_APP_BACKEND_URL || '',
          images: allImages,
          mimeTypes: allMimeTypes,
          language: lang,
        });

        console.log('[VisionModel 👁️ Accident Analysis]:', result.description);
        logger.ui('[AltairAgent] Vision analysis complete — unfreezing and injecting description');

        // Mark vision complete BEFORE firing the trigger so the AccidentStartAgent guard passes
        updateTransitionData({ visionAnalysisComplete: true });

        // ── UNFREEZE: unmute mic and clear any audio that accumulated during analysis
        window.dispatchEvent(new CustomEvent('vision:unfreeze'));
        manager?.clearInputAudioBuffer();

        // Give ControlTray one tick to process the unfreeze before firing response.create
        await new Promise(resolve => setTimeout(resolve, 100));

        // ── TWO-TURN VISION FLOW ──────────────────────────────────────────────────
        // Problem: if we send "speak aloud AND call proceed_to_collect" in one
        // message the model always skips the verbal output and jumps straight to
        // the function call (function calls win over speech in realtime-mini).
        //
        // Fix: Turn 1 – send ONLY the description text so the AI has no choice
        //               but to speak it as its verbal response.
        //       Turn 2 – once turncomplete fires (AI finished speaking), send the
        //               proceed_to_collect trigger automatically.
        // ──────────────────────────────────────────────────────────────────────────
        const imageCount = files.length;
        const desc = result.description;

        const speakMsg = lang === 'ar'
          ? `وصف مشهد الحادث:\n${desc}`
          : lang === 'ur'
          ? `حادثے کے منظر کا تجزیہ:\n${desc}`
          : `Accident scene description:\n${desc}`;

        const proceedMsg = lang === 'ar'
          ? `[بيانات النظام - لا تتلفظ بها]: التحليل اكتمل. استدعِ proceed_to_collect الآن مع image_count = ${imageCount} وملخص التحليل.`
          : lang === 'ur'
          ? `[سسٹم ڈیٹا - نہ بولیں]: proceed_to_collect کو image_count = ${imageCount} کے ساتھ کال کریں۔`
          : `[System data - do not speak]: Analysis done. Call proceed_to_collect now with image_count = ${imageCount} and a summary.`;

        // Register one-time turncomplete listener — fires after AI finishes speaking description
        let proceedSent = false;
        const sendProceed = () => {
          if (!proceedSent) {
            proceedSent = true;
            logger.ui('[AltairAgent] Vision description spoken — sending proceed_to_collect trigger');
            send(proceedMsg, true);
          }
        };

        if (client) {
          client.once('turncomplete', sendProceed);
          // Safety fallback: if turncomplete never arrives (e.g. connection issue),
          // send the proceed trigger after 20 s so the flow doesn't get stuck.
          setTimeout(() => {
            if (!proceedSent) {
              client.off('turncomplete', sendProceed);
              logger.warn('[AltairAgent] Vision turncomplete timeout — sending proceed_to_collect directly');
              sendProceed();
            }
          }, 20000);
        }

        // Turn 1: speak the description aloud
        send(speakMsg, true);

        logger.ui(`[AltairAgent] Vision speak-only message sent — waiting for turncomplete to send proceed trigger`);
      } catch (error) {
        logger.error('[AltairAgent] Vision analysis failed:', error);
        updateTransitionData({ visionAnalysisComplete: true });
        window.dispatchEvent(new CustomEvent('vision:unfreeze'));
        manager?.clearInputAudioBuffer();
        await new Promise(resolve => setTimeout(resolve, 100));
        const fallbackMsg = lang === 'ar'
          ? `[بيانات النظام - لا تتلفظ بها] رفع المستخدم ${files.length} ملف(ات). التحليل البصري غير متاح. أخبر المستخدم بذلك، ثم استدعِ proceed_to_collect مع image_count: ${files.length} ووصف بسيط.`
          : lang === 'ur'
          ? `[سسٹم ڈیٹا - نہ بولیں] ${files.length} فائل(یں) اپ لوڈ ہوئیں، وژن تجزیہ دستیاب نہیں۔ صارف کو بتائیں، پھر proceed_to_collect کو image_count: ${files.length} کے ساتھ کال کریں۔`
          : `[System data - do not speak] User uploaded ${files.length} file(s). Vision analysis unavailable. Inform the user, then call proceed_to_collect with image_count: ${files.length} and a simple description.`;
        send(fallbackMsg, true);
      } finally {
        setIsProcessingImages(false);
        setProcessingImageLabel('');
      }
    }
  };
  
  /**
   * Called by AccidentDataReview's Confirm button — receives the (possibly edited) data
   * and submits the report immediately.
   */
  const handleAccidentReviewConfirm = async (confirmedData: {
    accident_type: string;
    user_name: string;
    car_model: string;
    description: string;
  }) => {
    updateTransitionData({ accidentData: confirmedData });
    await handleAccidentSubmit(confirmedData);
  };

  const handleAccidentSubmit = async (overrideData?: {
    accident_type: string;
    user_name: string;
    car_model: string;
    description: string;
  }) => {
    logger.ui('[AltairAgent] Submitting accident report...');

    const accidentData = overrideData || context?.transitionData.accidentData;

    if (!accidentData || !userData) {
      logger.error('[AltairAgent] Missing accident data');
      return;
    }
    
    try {
      // Create form data with images
      const formData = new FormData();
      formData.append('accident_type', accidentData.accident_type || 'minor_accident');
      formData.append('location', context?.transitionData.userLocation?.address || '');
      formData.append('description', accidentData.description || '');
      formData.append('user_name', accidentData.user_name || userData.username);
      formData.append('car_model', accidentData.car_model || '');
      
      // Append images
      uploadedImages.forEach((file, index) => {
        formData.append(`image_${index}`, file);
      });
      
      // For now, simulate success (actual API integration can be added later)
      logger.info('[AltairAgent] Would submit accident report with data:', accidentData);
      
      const result = {
        success: true,
        report_number: `ACC-${Date.now()}`,
        message: detectedLanguage === 'ar' 
          ? 'تم تقديم البلاغ بنجاح. سيتواصل معك فريق ساعد قريباً.' 
          : 'Report submitted successfully. SAAED team will contact you soon.',
      };
      
      setAccidentResult(result);
      setShowAccidentResult(true);
      
      // Hide confirmation screen, show result screen
      setShowAccidentConfirm(false);
      
      // Update transition data with result
      updateTransitionData({ accidentResult: result });
      
      // Send explicit system message that triggers AI to respond automatically
      const resultMessage = detectedLanguage === 'ar'
        ? `[System] تم تقديم البلاغ بنجاح! رقم البلاغ: ${result.report_number}. الآن أعلن النجاح للمستخدم بصوت عالٍ وقدم الخيارات: 1) بلاغ جديد (new_report) 2) إنهاء (finish).`
        : `[System] Report submitted successfully! Report number: ${result.report_number}. Now announce the success to the user out loud and provide options: 1) New report (new_report) 2) Finish (finish).`;
      
      // Send with true flag to trigger AI response
      send(resultMessage, true);
      
    } catch (error) {
      logger.error('[AltairAgent] Accident submission failed:', error);
      const errorResult = {
        success: false,
        error: true,
        message: detectedLanguage === 'ar' 
          ? 'فشل تقديم البلاغ. يرجى المحاولة مرة أخرى.' 
          : 'Failed to submit report. Please try again.',
      };
      setAccidentResult(errorResult);
      setShowAccidentResult(true);
      
      // Hide confirmation screen, show result screen
      setShowAccidentConfirm(false);
      
      // Update transition data with error result
      updateTransitionData({ accidentResult: errorResult });
      
      // Send error message that triggers AI response
      const errorMessage = detectedLanguage === 'ar'
        ? `[System] فشل تقديم البلاغ. الرسالة: ${errorResult.message}. الآن أعلن الخطأ للمستخدم بصوت عالٍ وقدم الخيارات: 1) المحاولة مرة أخرى 2) إنهاء (finish).`
        : `[System] Report submission failed. Message: ${errorResult.message}. Now announce the error to the user out loud and provide options: 1) Try again 2) Finish (finish).`;
      
      // Send with true flag to trigger AI response
      send(errorMessage, true);
    }
  };
  
  const handleNewAccidentReport = async () => {
    // Reset accident data and start fresh
    setUploadedImages([]);
    setAccidentResult(null);
    updateTransitionData({ accidentData: undefined });
    await transitionTo('accident_start_agent', 'new_report');
  };
  
  // === LOST REPORT FLOW HANDLERS ===
  
  /**
   * Handle license image upload - sends image immediately to vision model (same agent, no transition)
   */
  const handleLicenseImageUpload = async (file: File) => {
    logger.ui('[AltairAgent] License image uploaded:', file.name);

    const lang = (detectedLanguage || 'ar') as 'ar' | 'en' | 'ur';

    // Show processing indicator and tell AI to hold until analysis is done
    const waitPhrase = getProcessingWaitPhrase(lang);
    const processingLabel = lang === 'ar'
      ? '⏳ جاري تحليل صورة الرخصة...'
      : lang === 'ur'
      ? '⏳ لائسنس کی تصویر کا تجزیہ جاری ہے...'
      : '⏳ Analyzing license image...';

    setIsProcessingImages(true);
    setProcessingImageLabel(processingLabel);

    // Instruct AI to respond with a "wait" phrase if user speaks while analysis is running
    send(
      `[System info - do not read aloud] License image analysis is now in progress. ` +
      `IMPORTANT: If the user speaks or asks anything BEFORE you receive the analysis results, ` +
      `respond ONLY with: "${waitPhrase}" — do not proceed with any flow step until analysis is complete.`,
      false
    );

    try {
      const base64 = await fileToBase64(file);

      logger.ui('[AltairAgent] Sending license image to vision model for plate extraction');

      const result = await extractLicensePlate({
        backendUrl: config.REACT_APP_BACKEND_URL || '',
        images: [base64],
        mimeTypes: [file.type || 'image/jpeg'],
        language: lang,
      });

      // Always print raw vision output to console for debugging
      console.log('[VisionModel 👁️ License Extraction]:', result.description);
      logger.ui('[AltairAgent] License plate vision analysis complete');

      // Inject vision description silently, then immediately trigger the AI so it processes
      // the license data right away without waiting for user to speak
      send(result.context_message, false);
      send('[System info - do not read aloud] License image analysis is now complete. Call the extract_license_data tool with the extracted values. You may proceed normally.', true);

    } catch (error) {
      logger.error('[AltairAgent] Error processing license image:', error);
      // Fallback: ask user to enter data manually
      send(
        '[System info - do not read aloud] User uploaded vehicle license image but vision analysis failed.',
        false
      );
      send('[System] Vision analysis failed. Please ask the user to enter the plate details manually.', true);
    } finally {
      setIsProcessingImages(false);
      setProcessingImageLabel('');
    }
  };
  
  const handleLicenseUploadComplete = () => {
    logger.ui('[AltairAgent] License upload marked complete by user');
    // Trigger AI to process the license data (vision context already injected on upload)
    send('[System info - do not read aloud] User confirmed license upload is complete. If image analysis is available, call extract_license_data now. If not, ask the user to enter data manually.', true);
  };
  
  const handleLicenseDataConfirm = async (confirmedData: any) => {
    logger.ui('[AltairAgent] License data confirmed:', confirmedData);
    
    // Validate confirmed data has all required fields
    if (!confirmedData.plateNumber || confirmedData.plateNumber.trim() === '') {
      logger.error('[AltairAgent] Plate number is missing from confirmed data');
      send('[System] Error: Plate number is required. Please ask the user to enter the plate number.', true);
      return;
    }
    
    try {
      // Map the extracted data to IDs using mapPlateData utility
      const mapped = await mapPlateData(
        confirmedData.plateColor,
        confirmedData.plateNumber,
        confirmedData.plateSource,
        confirmedData.plateCategory
      );
      
      logger.ui('[AltairAgent] Mapped plate data:', mapped);
      logger.ui('[AltairAgent] Plate number in mapped data:', mapped.plateNumber);
      
      // Store in state AND ref BEFORE transitioning (ref for reliable access in callbacks)
      setConfirmedMappedIds(mapped);
      confirmedMappedIdsRef.current = mapped;
      setExtractedLicenseData(confirmedData);
      setShowLicenseReview(false);
      
      // Update context and transition
      updateTransitionData({ licenseData: { ...confirmedData, mappedIds: mapped } });
      await transitionTo('lost_report_search_agent', 'data_confirmed', { 
        licenseData: { ...confirmedData, mappedIds: mapped } 
      });
      
    } catch (error) {
      logger.error('[AltairAgent] Failed to map plate data:', error);
      send(`[System] Error processing plate data. Please ask the user to re-upload the license image or enter data manually.`, true);
    }
  };
  
  const handleDateRangeSearch = async (startDate: string, endDate: string) => {
    logger.ui('[AltairAgent] Searching reports from', startDate, 'to', endDate);
    logger.ui('[AltairAgent] confirmedMappedIds state:', confirmedMappedIds);
    logger.ui('[AltairAgent] confirmedMappedIdsRef current:', confirmedMappedIdsRef.current);
    
    // Use ref as primary source (more reliable in callbacks), fall back to state
    const mappedIds = confirmedMappedIdsRef.current || confirmedMappedIds;
    
    if (!mappedIds) {
      logger.error('[AltairAgent] No confirmed license IDs for search');
      send('[System] Error: No license data confirmed. Please ask the user to restart by uploading their license image again.', true);
      return;
    }
    
    // Validate required fields before making API call
    const missingFields: string[] = [];
    if (!mappedIds.plateNumber || mappedIds.plateNumber.trim() === '') {
      missingFields.push('Plate Number');
    }
    if (!mappedIds.sourceId) {
      missingFields.push('Plate Source');
    }
    if (!mappedIds.categoryId) {
      missingFields.push('Plate Category');
    }
    if (!mappedIds.colorId) {
      missingFields.push('Plate Color');
    }
    
    if (missingFields.length > 0) {
      logger.error('[AltairAgent] Missing required fields:', missingFields);
      send(`[System] Error: Missing required data (${missingFields.join(', ')}). Please ask the user to re-upload a clearer license image so all fields can be extracted.`, true);
      // Show the license review again to fix data
      setShowDateRangePicker(false);
      setShowLicenseReview(true);
      return;
    }
    
    setIsSearching(true);
    setShowDateRangePicker(false);
    
    try {
      // Construct API request using validated mappedIds
      const request: SearchReportsRequest = {
        ReportDateFrom: startDate,
        ReportDateTo: endDate,
        PlateSourceId: mappedIds.sourceId,
        PlateNumber: mappedIds.plateNumber,
        ItemRegistrationPlateCategoryId: mappedIds.categoryId,
        ItemRegistrationPlateColorId: mappedIds.colorId,
        ItemRegistrationPlateKindId: mappedIds.categoryId,
      };
      
      logger.api('[AltairAgent] Search API request:', request);
      
      const response = await searchAccidentReports(request);
      logger.api('[AltairAgent] Search API response:', response);
      
      if (response.Count === 1) {
        // Enrich the report with labels
        const enrichedReports = await enrichReportsWithLabels(response.Data, detectedLanguage);
        setSearchResults({ Count: response.Count, Data: enrichedReports });
        setShowReportsList(true);
        
        // Transition to result agent with data — the agent's initialPrompt + context handle the announcement
        await transitionTo('lost_report_result_agent', 'search_complete', {
          searchResults: { count: response.Count, data: enrichedReports, startDate, endDate },
        });
        
      } else {
        // 0 or 2+ reports - show call SAAED
        setReportCountForCall(response.Count);
        setShowCallSaaed(true);
        setShowReportsList(false); // Ensure reports list is hidden for multiple reports
        
        // Transition to result agent — the agent's initialPrompt + context handle the announcement
        await transitionTo('lost_report_result_agent', 'search_complete', {
          searchResults: { count: response.Count, startDate, endDate },
        });
      }
      
    } catch (error) {
      logger.error('[AltairAgent] Search failed:', error);
      send(`[System] Search failed due to a technical error. Please apologize to the user and ask them to try again or call SAAED at 800-72233.`, true);
    } finally {
      setIsSearching(false);
    }
  };
  
  const handleRestartLostReportFlow = async () => {
    logger.ui('[AltairAgent] Restarting lost report flow');
    // Reset all lost report state
    setExtractedLicenseData(null);
    setConfirmedMappedIds(null);
    confirmedMappedIdsRef.current = null;
    setSearchResults(null);
    setShowReportsList(false);
    setShowCallSaaed(false);
    updateTransitionData({ 
      licenseData: undefined, 
      searchResults: undefined 
    });
    await transitionTo('lost_report_upload_extract_agent', 'new_search');
  };
  
  const handleStartReportRenewalFromLost = async (reportNumber: string) => {
    logger.ui('[AltairAgent] Starting renewal from lost report:', reportNumber);
    // Clear lost report data and transition to renewal with prefilled report number
    updateTransitionData({
      licenseData: undefined,
      searchResults: undefined,
      prefilledReportNumber: reportNumber,
    });
    await transitionTo('renewal_input_agent', 'start_renewal');
  };
  
  // === RENDER ===
  
  return (
    <div className="altair-agent">
      {/* Debug info */}
      {process.env.NODE_ENV === 'development' && (
        <div style={{
          position: 'fixed',
          top: 10,
          right: 10,
          padding: 10,
          backgroundColor: 'rgba(0,0,0,0.8)',
          color: 'white',
          fontSize: 12,
          borderRadius: 4,
          zIndex: 9999,
        }}>
          <div>Agent: {currentAgentId || 'none'}</div>
          <div>Connected: {connected ? '✓' : '✗'}</div>
          <div>Language: {detectedLanguage}</div>
          <div>Transitioning: {isTransitioning ? '...' : 'no'}</div>
        </div>
      )}
      
      {/* Vision processing banner — shown while image analysis is running */}
      {isProcessingImages && (
        <div style={{
          position: 'fixed',
          bottom: 100,
          left: '50%',
          transform: 'translateX(-50%)',
          padding: '12px 24px',
          backgroundColor: '#1a1a2e',
          color: '#ffffff',
          borderRadius: 30,
          fontSize: 15,
          fontWeight: 600,
          zIndex: 9999,
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          boxShadow: '0 4px 20px rgba(0,0,0,0.4)',
          whiteSpace: 'nowrap',
        }}>
          <span style={{ animation: 'spin 1s linear infinite', display: 'inline-block' }}>🔍</span>
          {processingImageLabel}
        </div>
      )}

      {/* Report Renewal - Step 1: Report Input */}
      {showReportInput && (
        <div style={{ marginTop: 20, marginBottom: 80 }}>
          <ReportInput
            onSubmit={handleReportNumberSubmit}
            language={detectedLanguage}
          />
          
          {/* Loading indicator */}
          {isLoadingReport && (
            <div style={{
              marginTop: 15,
              padding: 15,
              backgroundColor: '#e3f2fd',
              borderRadius: 8,
              textAlign: 'center',
              color: '#1565c0'
            }}>
              {detectedLanguage === 'ar' 
                ? '⏳ جاري البحث عن التقرير...' 
                : detectedLanguage === 'ur'
                ? '⏳ رپورٹ تلاش کی جا رہی ہے...'
                : '⏳ Searching for report...'}
            </div>
          )}
          
          {/* Error message */}
          {reportError && (
            <div style={{
              marginTop: 15,
              padding: 15,
              backgroundColor: '#ffebee',
              border: '1px solid #ef5350',
              borderRadius: 8,
              color: '#c62828',
              fontWeight: 500
            }}>
              ⚠️ {reportError}
            </div>
          )}
        </div>
      )}
      
      {/* Report Renewal - Step 2: Vehicle Selection */}
      {showVehicleSelector && context?.transitionData.reportData?.Vehicles && (
        <div style={{ marginTop: 20, marginBottom: 80 }}>
          <VehicleSelector
            vehicles={context.transitionData.reportData.Vehicles}
            onSelect={handleVehicleSelect}
            language={detectedLanguage}
            selectedVehicle={context.transitionData.selectedVehicle}
          />
        </div>
      )}
      
      {/* Report Renewal - Step 3: Confirmation */}
      {showConfirmation && context?.transitionData.selectedVehicle && (
        <div style={{
          marginTop: 20,
          marginBottom: 80,
          padding: 20,
          backgroundColor: '#f8f9fa',
          borderRadius: 8,
          border: '1px solid #e0e0e0',
        }}>
          <h3 style={{ color: '#2d5016', marginBottom: 15 }}>
            {detectedLanguage === 'ar' ? 'تأكيد البيانات' : 'Confirm Information'}
          </h3>
          
          <div style={{ marginBottom: 20 }}>
            <p><strong>{detectedLanguage === 'ar' ? 'السيارة:' : 'Vehicle:'}</strong></p>
            <p>
              {detectedLanguage === 'ar' ? 'لوحة' : 'Plate'} {context.transitionData.selectedVehicle.Plate.Number}
            </p>
            
            <p style={{ marginTop: 10 }}>
              <strong>{detectedLanguage === 'ar' ? 'الاسم:' : 'Name:'}</strong> {userData?.username}
            </p>
            <p>
              <strong>{detectedLanguage === 'ar' ? 'الموبايل:' : 'Mobile:'}</strong> {userData?.mobileNumber}
            </p>
          </div>
          
          <div style={{ display: 'flex', gap: 10 }}>
            <button
              onClick={handleRenewalConfirm}
              style={{
                flex: 1,
                padding: '12px 20px',
                backgroundColor: '#2d5016',
                color: 'white',
                border: 'none',
                borderRadius: 6,
                cursor: 'pointer',
                fontSize: 16,
                fontWeight: 600,
              }}
            >
              {detectedLanguage === 'ar' ? 'تأكيد وإرسال' : 'Confirm & Submit'}
            </button>
            <button
              onClick={handleBackToMenu}
              style={{
                padding: '12px 20px',
                backgroundColor: '#6c757d',
                color: 'white',
                border: 'none',
                borderRadius: 6,
                cursor: 'pointer',
              }}
            >
              {detectedLanguage === 'ar' ? 'إلغاء' : 'Cancel'}
            </button>
          </div>
        </div>
      )}
      
      {/* Report Renewal - Step 4: Success Result */}
      {showResult && context?.transitionData.renewalResult && 
       context.transitionData.renewalResult.RequestId && 
       context.transitionData.renewalResult.RequestId !== 0 && 
       !context.transitionData.renewalResult.error && (
        <div style={{
          marginTop: 20,
          marginBottom: 80,
          padding: 20,
          backgroundColor: '#e8f5e8',
          borderRadius: 8,
          border: '1px solid #4caf50',
        }}>
          <h3 style={{ color: '#2d5016', marginBottom: 15 }}>
            {detectedLanguage === 'ar' ? 'تم تقديم الطلب بنجاح' : 
             detectedLanguage === 'ur' ? 'درخواست کامیابی سے جمع کرا دی گئی' : 
             'Request Submitted Successfully'}
          </h3>
          
          <div style={{ color: '#1a1a1a' }}>
            <p>
              <strong style={{ color: '#2d5016' }}>
                {detectedLanguage === 'ar' ? 'رقم الطلب:' : 
                 detectedLanguage === 'ur' ? 'درخواست نمبر:' : 
                 'Request ID:'}
              </strong>{' '}
              {context.transitionData.renewalResult.RequestId}
            </p>
            
            {context.transitionData.renewalResult.RequestDate && (
              <p>
                <strong style={{ color: '#2d5016' }}>
                  {detectedLanguage === 'ar' ? 'تاريخ الطلب:' : 
                   detectedLanguage === 'ur' ? 'درخواست کی تاریخ:' : 
                   'Request Date:'}
                </strong>{' '}
                {new Date(context.transitionData.renewalResult.RequestDate).toLocaleString(
                  detectedLanguage === 'ar' ? 'ar-AE' : 
                  detectedLanguage === 'ur' ? 'ur-PK' : 'en-US'
                )}
              </p>
            )}
            
            {context.transitionData.renewalResult.Message && (
              <p style={{ 
                marginTop: 15, 
                padding: 10, 
                backgroundColor: '#d4edda', 
                borderRadius: 4, 
                color: '#155724' 
              }}>
                {context.transitionData.renewalResult.Message}
              </p>
            )}
            
            {/* Payment Link Section */}
            {context.transitionData.renewalResult.URLMessage ? (
              <div style={{
                marginTop: 20,
                padding: 15,
                backgroundColor: '#fff3cd',
                borderRadius: 8,
                border: '1px solid #ffc107',
              }}>
                <p style={{ fontWeight: 600, color: '#856404', marginBottom: 10 }}>
                  {detectedLanguage === 'ar' ? 'رابط الدفع جاهز!' : 
                   detectedLanguage === 'ur' ? 'ادائیگی کا لنک تیار ہے!' : 
                   'Payment Link Ready!'}
                </p>
                <a
                  href={context.transitionData.renewalResult.URLMessage}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    display: 'block',
                    padding: '12px 20px',
                    backgroundColor: '#2d5016',
                    color: 'white',
                    textAlign: 'center',
                    borderRadius: 6,
                    textDecoration: 'none',
                    fontWeight: 600,
                  }}
                >
                  {detectedLanguage === 'ar' ? 'اضغط للدفع' : 
                   detectedLanguage === 'ur' ? 'ادائیگی کے لیے کلک کریں' : 
                   'Click to Pay'}
                </a>
              </div>
            ) : (
              <p style={{ 
                marginTop: 15, 
                padding: 10, 
                backgroundColor: '#e3f2fd', 
                borderRadius: 4, 
                color: '#1565c0' 
              }}>
                {detectedLanguage === 'ar' ? 'سيتم إرسال رابط الدفع إلى رقم الجوال المسجل.' : 
                 detectedLanguage === 'ur' ? 'ادائیگی کا لنک آپ کے رجسٹرڈ موبائل نمبر پر بھیجا جائے گا۔' : 
                 'Payment link will be sent to your registered mobile number.'}
              </p>
            )}
          </div>
          
          <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
            <button
              onClick={handleNewRequest}
              style={{
                padding: '12px 20px',
                backgroundColor: '#0066FF',
                color: 'white',
                border: 'none',
                borderRadius: 6,
                cursor: 'pointer',
                fontWeight: 600,
              }}
            >
              {detectedLanguage === 'ar' ? 'طلب جديد' : 
               detectedLanguage === 'ur' ? 'نئی درخواست' : 
               'New Request'}
            </button>
            <button
              onClick={handleBackToMenu}
              style={{
                padding: '12px 20px',
                backgroundColor: '#6c757d',
                color: 'white',
                border: 'none',
                borderRadius: 6,
                cursor: 'pointer',
              }}
            >
              {detectedLanguage === 'ar' ? 'القائمة الرئيسية' : 
               detectedLanguage === 'ur' ? 'مین مینو' : 
               'Main Menu'}
            </button>
          </div>
        </div>
      )}
      
      {/* Report Renewal - Step 4: Error Result */}
      {showResult && context?.transitionData.renewalResult && 
       (context.transitionData.renewalResult.error || 
        !context.transitionData.renewalResult.RequestId || 
        context.transitionData.renewalResult.RequestId === 0) && (
        <div style={{
          marginTop: 20,
          marginBottom: 80,
          padding: 20,
          backgroundColor: '#ffebee',
          borderRadius: 8,
          border: '1px solid #ef5350',
        }}>
          <h3 style={{ color: '#c62828', marginBottom: 15 }}>
            {detectedLanguage === 'ar' ? 'حدث خطأ' : 
             detectedLanguage === 'ur' ? 'ایک خرابی ہوئی' : 
             'Error Occurred'}
          </h3>
          
          <p style={{ 
            padding: 10, 
            backgroundColor: '#ffcdd2', 
            borderRadius: 4, 
            color: '#b71c1c',
            fontWeight: 500
          }}>
            {context.transitionData.renewalResult.Message || 
             (detectedLanguage === 'ar' ? 'فشل تقديم الطلب. يرجى المحاولة مرة أخرى.' : 
              detectedLanguage === 'ur' ? 'درخواست جمع کرانے میں ناکامی۔ براہ کرم دوبارہ کوشش کریں۔' : 
              'Failed to submit request. Please try again.')}
          </p>
          
          <div style={{ display: 'flex', gap: 10, marginTop: 15 }}>
            <button
              onClick={handleNewRequest}
              style={{
                padding: '12px 20px',
                backgroundColor: '#0066FF',
                color: 'white',
                border: 'none',
                borderRadius: 6,
                cursor: 'pointer',
                fontWeight: 600,
              }}
            >
              {detectedLanguage === 'ar' ? 'حاول مرة أخرى' : 
               detectedLanguage === 'ur' ? 'دوبارہ کوشش کریں' : 
               'Try Again'}
            </button>
            <button
              onClick={handleBackToMenu}
              style={{
                padding: '12px 20px',
                backgroundColor: '#6c757d',
                color: 'white',
                border: 'none',
                borderRadius: 6,
                cursor: 'pointer',
              }}
            >
              {detectedLanguage === 'ar' ? 'القائمة الرئيسية' : 
               detectedLanguage === 'ur' ? 'مین مینو' : 
               'Main Menu'}
            </button>
          </div>
        </div>
      )}
      
      {/* === ACCIDENT FLOW UI === */}
      
      {/* Accident - Step 0: Option Selection */}
      {showAccidentOptions && (
        <div style={{ marginTop: 20, marginBottom: 80 }}>
          <AccidentOptions language={detectedLanguage} />
        </div>
      )}
      
      {/* Accident - Camera Arrow Indicator */}
      {showCameraArrow && (
        <CameraArrow language={detectedLanguage} />
      )}
      
      {/* Accident - Step 1: Image Upload */}
      {showImageUpload && (
        <div style={{ marginTop: 20, marginBottom: 80 }}>
          <ImageUpload
            onImagesUploaded={handleImagesUploaded}
            language={detectedLanguage}
          />
          <p style={{ 
            marginTop: 10, 
            color: '#666',
            textAlign: 'center' 
          }}>
            {detectedLanguage === 'ar' 
              ? 'بعد رفع الصور، قل "خلصت" للمتابعة.'
              : 'After uploading images, say "finished" to continue.'}
          </p>
        </div>
      )}
      
      {/* Accident - Step 3: Confirmation & Submit — editable review card */}
      {showAccidentConfirm && !showAccidentResult && context?.transitionData.accidentData && (
        <div style={{ marginTop: 20, marginBottom: 80 }}>
          <AccidentDataReview
            accidentData={context.transitionData.accidentData}
            location={context?.transitionData.userLocation?.address}
            imageCount={uploadedImages.length}
            onConfirm={handleAccidentReviewConfirm}
            onCancel={handleBackToMenu}
            language={detectedLanguage as 'ar' | 'en' | 'ur'}
          />
        </div>
      )}
      
      {/* Accident Result - only show if NOT showing confirmation */}
      {showAccidentResult && !showAccidentConfirm && accidentResult && (
        <div style={{
          marginTop: 20,
          marginBottom: 80,
          padding: 20,
          backgroundColor: accidentResult.success ? '#e8f5e8' : '#ffebee',
          borderRadius: 8,
          border: `1px solid ${accidentResult.success ? '#4caf50' : '#ef5350'}`,
        }}>
          <h3 style={{ color: accidentResult.success ? '#2d5016' : '#c62828', marginBottom: 15 }}>
            {accidentResult.success 
              ? (detectedLanguage === 'ar' ? 'تم تقديم البلاغ بنجاح' : 'Report Submitted Successfully')
              : (detectedLanguage === 'ar' ? 'حدث خطأ' : 'Error Occurred')}
          </h3>
          
          {accidentResult.report_number && (
            <p><strong>{detectedLanguage === 'ar' ? 'رقم البلاغ:' : 'Report Number:'}</strong> {accidentResult.report_number}</p>
          )}
          
          <p style={{ marginTop: 10 }}>{accidentResult.message}</p>
          
          <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
            <button
              onClick={handleNewAccidentReport}
              style={{
                padding: '12px 20px',
                backgroundColor: '#0066FF',
                color: 'white',
                border: 'none',
                borderRadius: 6,
                cursor: 'pointer',
                fontWeight: 600,
              }}
            >
              {detectedLanguage === 'ar' ? 'بلاغ جديد' : 'New Report'}
            </button>
            <button
              onClick={handleBackToMenu}
              style={{
                padding: '12px 20px',
                backgroundColor: '#6c757d',
                color: 'white',
                border: 'none',
                borderRadius: 6,
                cursor: 'pointer',
              }}
            >
              {detectedLanguage === 'ar' ? 'القائمة الرئيسية' : 'Main Menu'}
            </button>
          </div>
        </div>
      )}
      
      {/* === LOST REPORT FLOW UI === */}
      
      {/* Lost Report - Step 1: License Upload */}
      {showLicenseUpload && (
        <div style={{ marginTop: 20, marginBottom: 80 }}>
          <LicenseUpload
            onImageUploaded={handleLicenseImageUpload}
            language={detectedLanguage}
          />
          <p style={{ 
            marginTop: 10, 
            color: '#666',
            textAlign: 'center' 
          }}>
            {detectedLanguage === 'ar' 
              ? 'بعد رفع صورة الرخصة، قل "خلصت" للمتابعة.'
              : 'After uploading the license image, say "finished" to continue.'}
          </p>
        </div>
      )}
      
      {/* Lost Report - Step 2: License Data Review */}
      {showLicenseReview && extractedLicenseData && (
        <div style={{ marginTop: 20, marginBottom: 80 }}>
          <LicenseDataReview
            extractedData={extractedLicenseData}
            onConfirm={handleLicenseDataConfirm}
            language={detectedLanguage}
          />
          <button
            onClick={() => {
              setExtractedLicenseData(null);
              setShowLicenseReview(false);
              setShowLicenseUpload(true);
            }}
            style={{
              marginTop: 10,
              padding: '10px 20px',
              backgroundColor: '#6c757d',
              color: 'white',
              border: 'none',
              borderRadius: 6,
              cursor: 'pointer',
              display: 'block',
              margin: '10px auto 0',
            }}
          >
            {detectedLanguage === 'ar' ? 'رفع صورة جديدة' : 'Upload New Image'}
          </button>
        </div>
      )}
      
      {/* Lost Report - Step 3: Date Range Picker */}
      {showDateRangePicker && (
        <div style={{ marginTop: 20, marginBottom: 80 }}>
          <DateRangePicker
            onSearch={handleDateRangeSearch}
            onCancel={handleRestartLostReportFlow}
            language={detectedLanguage}
            isLoading={isSearching}
          />
        </div>
      )}
      
      {/* Lost Report - Step 4a: Reports List (1 report found) */}
      {showReportsList && searchResults && searchResults.Count === 1 && (
        <div style={{ marginTop: 20, marginBottom: 80 }}>
          <ReportsList
            reports={searchResults.Data}
            count={searchResults.Count}
            language={detectedLanguage}
          />
          
          {/* Action buttons for found report */}
          <div style={{ 
            display: 'flex', 
            gap: 10, 
            marginTop: 20,
            justifyContent: 'center',
            flexWrap: 'wrap'
          }}>
            <button
              onClick={handleRestartLostReportFlow}
              style={{
                padding: '12px 20px',
                backgroundColor: '#0066FF',
                color: 'white',
                border: 'none',
                borderRadius: 6,
                cursor: 'pointer',
                fontWeight: 600,
              }}
            >
              {detectedLanguage === 'ar' ? 'بحث جديد' : 'New Search'}
            </button>
            {searchResults.Data[0]?.ReportNumber && (
              <button
                onClick={() => handleStartReportRenewalFromLost(searchResults.Data[0].ReportNumber)}
                style={{
                  padding: '12px 20px',
                  backgroundColor: '#2d5016',
                  color: 'white',
                  border: 'none',
                  borderRadius: 6,
                  cursor: 'pointer',
                  fontWeight: 600,
                }}
              >
                {detectedLanguage === 'ar' ? 'تجديد التقرير' : 'Renew Report'}
              </button>
            )}
            <button
              onClick={handleBackToMenu}
              style={{
                padding: '12px 20px',
                backgroundColor: '#6c757d',
                color: 'white',
                border: 'none',
                borderRadius: 6,
                cursor: 'pointer',
              }}
            >
              {detectedLanguage === 'ar' ? 'خلصت' : 'Done'}
            </button>
          </div>
        </div>
      )}
      
      {/* Lost Report - Step 4b: Call SAAED Office (0 or 2+ reports) */}
      {showCallSaaed && (
        <div style={{ marginTop: 20, marginBottom: 80 }}>
          <CallSaaedOffice
            reportCount={reportCountForCall}
            language={detectedLanguage}
            onRestart={handleRestartLostReportFlow}
          />
        </div>
      )}
      
      {/* Searching indicator */}
      {isSearching && (
        <div style={{
          marginTop: 20,
          marginBottom: 80,
          padding: 20,
          textAlign: 'center',
          backgroundColor: '#e3f2fd',
          borderRadius: 8,
        }}>
          <p style={{ color: '#1565c0' }}>
            {detectedLanguage === 'ar' ? '⏳ جاري البحث عن التقارير...' : '⏳ Searching for reports...'}
          </p>
        </div>
      )}
    </div>
  );
}

export const AltairAgent = memo(AltairAgentComponent);
export default AltairAgent;

